/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { pgTable, serial, text, jsonb, timestamp, boolean, integer, unique, index } from 'drizzle-orm/pg-core';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { z } from 'zod';

// Agent Categories
export const AGENT_CATEGORIES = [
  'general_assistant',
  'curriculum_design',
  'instructional_design', 
  'assessment_design',
  'assessment_evaluation',
  'learning_activities',
  'feedback_grading',
  'content_creation',
  'tutoring',
  'workflow',
  'general',
  'custom',
  // System agent categories (automated oversight)
  'system_audit',       // Security, vulnerability scanning
  'system_measurement', // Coverage, metrics
  'system_review',      // Architecture, code quality
  'system_evaluation',  // Documentation, dependencies
] as const;

export type AgentCategory = typeof AGENT_CATEGORIES[number];

// Agent Status
export const AGENT_STATUS = ['active', 'inactive', 'draft', 'archived'] as const;
export type AgentStatus = typeof AGENT_STATUS[number];

// Agents table - stores agent configurations
export const agents = pgTable('agents', {
  id: serial('id').primaryKey(),
  name: text('name').notNull().unique(),
  displayName: text('display_name').notNull(),
  description: text('description'),
  category: text('category').notNull().$type<AgentCategory>(),
  
  // System prompt configuration
  systemPromptTemplate: text('system_prompt_template').notNull(),
  
  // Model configuration
  defaultModel: text('default_model'),
  modelConfig: jsonb('model_config'), // temperature, maxTokens, etc.
  
  // Tools configuration
  enabledTools: text('enabled_tools').array(), // Array of tool names
  toolConfig: jsonb('tool_config'), // Tool-specific configurations
  
  // Context requirements
  context: text('context').array().default([]), // Array of required context variable names
  
  // Memory configuration
  memoryEnabled: boolean('memory_enabled').default(true),
  memoryConfig: jsonb('memory_config'), // Memory-specific settings
  
  // Avatar and voice configuration
  avatarEnabled: boolean('avatar_enabled').default(false),
  avatarUrl: text('avatar_url'),
  voiceConfig: jsonb('voice_config'),
  
  // Library organization
  library: text('library'), // Library name (same as subfolder name under data/agents)
  
  // Permissions and ownership
  createdBy: integer('created_by'), // User ID who created the agent
  isPublic: boolean('is_public').default(false), // Whether agent is available to all users
  isSystem: boolean('is_system').default(false), // System agents cannot be deleted/modified by users
  
  // Status and metadata
  status: text('status').notNull().$type<AgentStatus>().default('active'),
  isEnabledInSidebar: boolean('is_enabled_in_sidebar').default(true), // Whether agent appears in sidebar dropdown
  welcomeMessage: text('welcome_message'), // Custom welcome message shown when agent is selected in sidebar
  tags: text('tags').array(),
  metadata: jsonb('metadata'), // Additional custom metadata
  
  // Timestamps
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
}, (table) => ({
  categoryIdx: index('agents_category_idx').on(table.category),
  statusIdx: index('agents_status_idx').on(table.status),
  createdByIdx: index('agents_created_by_idx').on(table.createdBy),
}));

// Agent Templates - predefined agent configurations
export const agentTemplates = pgTable('agent_templates', {
  id: serial('id').primaryKey(),
  name: text('name').notNull().unique(),
  displayName: text('display_name').notNull(),
  description: text('description'),
  category: text('category').notNull().$type<AgentCategory>(),
  
  // Template configuration (same as agents but serves as a template)
  config: jsonb('config').notNull(), // Full agent configuration
  
  // Template metadata
  icon: text('icon'), // Icon identifier or URL
  preview: text('preview'), // Preview text or image
  examples: jsonb('examples'), // Example use cases
  
  isSystem: boolean('is_system').default(true),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// Agent Sessions - tracks agent conversation sessions with chat and Honcho integration
// Agent Sessions is used for non-chat sessions. For chat sessions use chatSessions from chat.ts instead.
export const agentSessions = pgTable('agent_sessions', {
  id: serial('id').primaryKey(),
  sessionId: text('session_id').notNull().unique(), // External session ID (e.g., Honcho)
  agentId: integer('agent_id').notNull().references(() => agents.id),
  userId: integer('user_id').notNull(),
  
  // Honcho integration state
  honchoWorkspaceId: text('honcho_workspace_id'), // Honcho workspace ID
  honchoSessionId: text('honcho_session_id'), // Honcho session ID  
  honchoUserPeerId: text('honcho_user_peer_id'), // User's peer ID in Honcho
  
  // Session metadata
  title: text('title'), // Optional session title (auto-generated or user-set)
  description: text('description'), // Optional session description
  
  // UI State (persisted for session continuity)
  isExpanded: boolean('is_expanded').default(false),
  sidebarWidth: integer('sidebar_width').default(320),
  
  // Session context
  context: jsonb('context'), // Session-specific context
  metadata: jsonb('metadata'), // Additional metadata
  basePath: text('base_path'), // Base directory for resolving relative paths in MCP tools
  
  // Session state
  isActive: boolean('is_active').default(true),
  isInitialized: boolean('is_initialized').default(false),
  startedAt: timestamp('started_at').defaultNow(),
  endedAt: timestamp('ended_at'),
  lastActivityAt: timestamp('last_activity_at').defaultNow(),
  
  // Statistics (denormalized for performance)
  messageCount: integer('message_count').default(0),
  toolCallsCount: integer('tool_calls_count').default(0),
  totalTokensUsed: integer('total_tokens_used').default(0),
  totalProcessingTime: integer('total_processing_time').default(0), // in milliseconds
  entityLinksCount: integer('entity_links_count').default(0),
}, (table) => ({
  agentIdIdx: index('agent_sessions_agent_idx').on(table.agentId),
  userIdIdx: index('agent_sessions_user_idx').on(table.userId),
  sessionIdIdx: index('agent_sessions_session_idx').on(table.sessionId),
  honchoSessionIdx: index('agent_sessions_honcho_session_idx').on(table.honchoSessionId),
  lastActivityIdx: index('agent_sessions_last_activity_idx').on(table.lastActivityAt),
}));

// Agent Executions - tracks individual agent execution requests with real-time progress
// Follows the same pattern as learningActionPlanExecutions for consistency
export const agentExecutions = pgTable('agent_executions', {
  id: text('id').primaryKey(), // Execution ID (same as executionId)
  executionId: text('execution_id').notNull().unique(), // Unique execution identifier
  
  // Agent and user info
  agentId: integer('agent_id').references(() => agents.id),
  agentName: text('agent_name'), // Either agentId or agentName must be provided
  userId: integer('user_id').notNull(),
  
  // Request data
  message: text('message').notNull(), // User's message to the agent
  sessionId: text('session_id'), // Optional chat session ID
  
  // Execution tracking
  status: text('status').notNull().default('pending'), // 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'
  progress: integer('progress').default(0), // Progress percentage 0-100
  currentStep: text('current_step'), // Current step description
  
  // Real-time execution data
  toolCalls: jsonb('tool_calls'), // Array of tool executions
  subAgentCalls: jsonb('sub_agent_calls'), // Array of sub-agent dispatches
  entityLinks: jsonb('entity_links'), // Array of created/updated entities
  
  // Final result and metadata
  result: jsonb('result'), // Complete execution result
  metadata: jsonb('metadata'), // File attachments, context, overrides, etc.
  
  // Timestamps
  startedAt: timestamp('started_at'),
  completedAt: timestamp('completed_at'),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
}, (table) => ({
  agentIdIdx: index('agent_executions_agent_idx').on(table.agentId),
  userIdIdx: index('agent_executions_user_idx').on(table.userId),
  statusIdx: index('agent_executions_status_idx').on(table.status),
  createdAtIdx: index('agent_executions_created_idx').on(table.createdAt),
}));

// User Agent Preferences - stores user-specific agent customizations
export const userAgentPreferences = pgTable('user_agent_preferences', {
  id: serial('id').primaryKey(),
  userId: integer('user_id').notNull(),
  agentId: integer('agent_id').notNull().references(() => agents.id, { onDelete: 'cascade' }),
  customName: text('custom_name'),
  customSystemPrompt: text('custom_system_prompt'),
  customTools: jsonb('custom_tools'), // Array of custom tool configurations
  customConfig: jsonb('custom_config'), // Custom model/memory configuration
  isEnabledInSidebar: boolean('is_enabled_in_sidebar'),
  lastUsedAt: timestamp('last_used_at'),
  usageCount: integer('usage_count').default(0),
  isFavorite: boolean('is_favorite').default(false),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
}, (table) => ({
  userAgentIdx: index('user_agent_preferences_user_agent_idx').on(table.userId, table.agentId),
  userIdx: index('user_agent_preferences_user_idx').on(table.userId),
  agentIdx: index('user_agent_preferences_agent_idx').on(table.agentId),
}));

// Registered Tools - stores HTTP proxy tool registrations from external applications
export const registeredTools = pgTable('registered_tools', {
  id: serial('id').primaryKey(),
  name: text('name').notNull().unique(),
  displayName: text('display_name'),
  description: text('description').notNull(),
  category: text('category').notNull().default('external'),
  inputSchema: jsonb('input_schema').notNull(), // JSON schema for tool inputs
  httpEndpoint: text('http_endpoint').notNull(),
  httpMethod: text('http_method').notNull().default('POST'),
  httpHeaders: jsonb('http_headers'), // Optional headers for HTTP requests
  timeoutMs: integer('timeout_ms').notNull().default(30000),
  library: text('library'), // Optional library grouping
  permissions: text('permissions').array(), // Required permissions
  tags: text('tags').array(),
  entityTypes: text('entity_types').array(), // Entity types this tool can operate on
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
}, (table) => ({
  nameIdx: index('registered_tools_name_idx').on(table.name),
  libraryIdx: index('registered_tools_library_idx').on(table.library),
  categoryIdx: index('registered_tools_category_idx').on(table.category),
}));

// Type exports
export type Agent = typeof agents.$inferSelect;
export type InsertAgent = typeof agents.$inferInsert;
export type AgentTemplate = typeof agentTemplates.$inferSelect;
export type InsertAgentTemplate = typeof agentTemplates.$inferInsert;
export type AgentSession = typeof agentSessions.$inferSelect;
export type InsertAgentSession = typeof agentSessions.$inferInsert;
export type AgentExecution = typeof agentExecutions.$inferSelect;
export type InsertAgentExecution = typeof agentExecutions.$inferInsert;
export type RegisteredTool = typeof registeredTools.$inferSelect;
export type InsertRegisteredTool = typeof registeredTools.$inferInsert;
export type UserAgentPreference = typeof userAgentPreferences.$inferSelect;
export type InsertUserAgentPreference = typeof userAgentPreferences.$inferInsert;

/**
 * Common interface for sessions that can be used with agent execution
 * Both AgentSession and ChatSession implement this interface
 */
export interface ExecutableSession {
  id: number;
  sessionId?: string;  // External session ID (for AgentSession compatibility)
  sessionKey?: string; // Internal session key (for ChatSession compatibility)
  userId: number;
  agentId?: number;    // Single agent (AgentSession) or current agent (ChatSession)
  context?: any;
  metadata?: any;
  isActive?: boolean | null;
  honchoWorkspaceId?: string | null;
  honchoSessionId?: string | null;
  honchoUserPeerId?: string | null;
  
  // Additional properties for compatibility
  title?: string | null;
  description?: string | null;
  isExpanded?: boolean | null;
  sidebarWidth?: number | null;
  messageCount?: number | null;
  toolCallsCount?: number | null;
  totalTokensUsed?: number | null;
  totalProcessingTime?: number | null;
  entityLinksCount?: number | null;
  startedAt?: Date | null;
  createdAt?: Date | null; // For backwards compatibility (chat sessions have both)
  endedAt?: Date | null;
  lastActivityAt?: Date | null;
  isInitialized?: boolean | null;
  
  // Base path for relative file path resolution in MCP tools
  basePath?: string | null; // Base directory for resolving relative paths (used by any app, not just Velocity)
}

/**
 * Utility functions for session compatibility
 * 
 * These methods use spread operators to copy all fields from the source session,
 * then only override the specific differences between session types. This makes
 * the code robust to schema changes - new fields are automatically included.
 */
export class SessionUtils {
  /**
   * Convert a ChatSession to an ExecutableSession for agent execution
   * 
   * Differences from base schema:
   * - currentAgentId -> agentId (field name difference)
   * - sessionKey -> sessionId (for compatibility with ExecutableSession interface)
   */
  static chatSessionToExecutable(chatSession: any): ExecutableSession {
    return {
      ...chatSession,                         // Copy all fields from ChatSession
      sessionId: chatSession.sessionKey,      // Map sessionKey to sessionId
      agentId: chatSession.currentAgentId,    // Map currentAgentId to agentId
    };
  }

  /**
   * Convert an AgentSession to an ExecutableSession
   * 
   * Differences from base schema:
   * - sessionId -> sessionKey (for compatibility with ExecutableSession interface)
   * - Add createdAt (map from startedAt for backwards compatibility)
   */
  static agentSessionToExecutable(agentSession: AgentSession): ExecutableSession {
    return {
      ...agentSession,                        // Copy all fields from AgentSession
      sessionKey: agentSession.sessionId,     // Map sessionId to sessionKey
      createdAt: agentSession.startedAt,      // Map startedAt to createdAt for backwards compatibility
    };
  }

  /**
   * Check if a session ID corresponds to a chat session (numeric) or agent session (string)
   */
  static isChatSessionId(sessionId: string): boolean {
    return /^\d+$/.test(sessionId);
  }
}

// Zod schemas for validation
export const insertAgentSchema = createInsertSchema(agents);
export const selectAgentSchema = createSelectSchema(agents);
export const insertAgentTemplateSchema = createInsertSchema(agentTemplates);
export const selectAgentTemplateSchema = createSelectSchema(agentTemplates);
export const insertAgentSessionSchema = createInsertSchema(agentSessions);
export const selectAgentSessionSchema = createSelectSchema(agentSessions);
export const insertAgentExecutionSchema = createInsertSchema(agentExecutions);
export const selectAgentExecutionSchema = createSelectSchema(agentExecutions);
export const insertRegisteredToolSchema = createInsertSchema(registeredTools);
export const selectRegisteredToolSchema = createSelectSchema(registeredTools);
export const insertUserAgentPreferenceSchema = createInsertSchema(userAgentPreferences);
export const selectUserAgentPreferenceSchema = createSelectSchema(userAgentPreferences);

// Agent configuration schema for runtime validation
export const AgentConfigSchema = z.object({
  systemPromptTemplate: z.string(),
  defaultModel: z.string().optional(),
  modelConfig: z.object({
    temperature: z.number().min(0).max(2).optional(),
    maxTokens: z.number().positive().optional(),
    topP: z.number().min(0).max(1).optional(),
    frequencyPenalty: z.number().min(-2).max(2).optional(),
    presencePenalty: z.number().min(-2).max(2).optional(),
  }).optional(),
  enabledTools: z.array(z.string()).optional(),
  toolConfig: z.record(z.any()).optional(),
  memoryEnabled: z.boolean().optional(),
  memoryConfig: z.object({
    maxMessages: z.number().positive().optional(),
    contextWindow: z.number().positive().optional(),
  }).optional(),
  avatarEnabled: z.boolean().optional(),
  avatarUrl: z.string().url().optional(),
  voiceConfig: z.object({
    voiceName: z.string().optional(),
    speed: z.number().min(0.5).max(2).optional(),
    pitch: z.number().min(-20).max(20).optional(),
  }).optional(),
});

export type AgentConfig = z.infer<typeof AgentConfigSchema>;